import React from 'react';
import { View, Text, Flex } from '@smarterservices/smarter-ui';
import KeyboardShortcutDisplay from '../KeyboardShortcutDisplay';

interface NavigationalMenuItemProps {
  children: React.ReactNode;
  shortcut?: string; // Single shortcut string like "Ctrl+C" or "B"
  
  // Layout props
  justifyContent?: 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  padding?: string;
  margin?: string;
  
  // Text styling props
  textSize?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
  textColor?: string;
  textWeight?: 'normal' | 'light' | 'bold';
  
  // Shortcut styling props
  shortcutTextVariant?: 'legend' | 'contentSmall' | 'contentImportant' | 'content' | 'descriptionSection';
  shortcutVariant?: 'light' | 'dark' | 'medium';
  showSeparator?: boolean;
  
  // Container styling
  background?: string;
  borderRadius?: string;
  border?: string;
  
  // Interaction
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
  
  // Accessibility
  role?: string;
  tabIndex?: number;
  'aria-label'?: string;
}

export function NavigationalMenuItem({
  children,
  shortcut,
  
  // Layout with defaults
  justifyContent = 'space-between',
  alignItems = 'center',
  padding = 'none',
  margin = 'none',
  
  // Text styling with defaults
  textSize = null,
  textColor = null,
  textWeight = null,
  
  // Shortcut styling with defaults
  shortcutTextVariant = 'legend',
  shortcutVariant = 'medium',
  showSeparator = false,
  
  // Container styling
  background,
  borderRadius,
  border,
  
  // Interaction
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled = false,
  
  // Accessibility
  role = 'menuitem',
  tabIndex = 0,
  'aria-label': ariaLabel,
}: NavigationalMenuItemProps) {
  
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      if (onClick) {
        onClick();
      }
    }
  };

  return (
    <Flex
      as="div"
      justifyItems={justifyContent}
      alignItems={alignItems}
      padding={padding}
      margin={margin}
      background={background}
      borderRadius={borderRadius}
      border={border}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeyDown}
      role={role}
      tabIndex={disabled ? -1 : tabIndex}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Flex.Item shouldGrow>
        <Text 
          size={textSize} 
          color={disabled ? 'secondary' : textColor} 
          weight={textWeight}
        >
          {children}
        </Text>
      </Flex.Item>
      
      {shortcut && (
        <Flex.Item>
          <KeyboardShortcutDisplay 
            shortcut={shortcut}
            textVariant={shortcutTextVariant}
            variant={shortcutVariant}
            showSeparator={showSeparator}
          />
        </Flex.Item>
      )}
    </Flex>
  );
}

export default NavigationalMenuItem;
